import type { ReactNode } from 'react';
import { Dialog } from '@base-ui/react/dialog';
import { ScrollArea } from '@components/ui/ScrollArea/ScrollArea';

interface IFormDialogShellProps {
  /** Omit for a host that is only mounted while open (DeliveryOrderDialog). */
  open?: boolean;
  onClose: () => void;
  title: string;
  cancelLabel: string;
  /** The host's css module — backdrop/popup/title/body/cancelBtn all `composes:`
   *  dialogChrome.module.css already; only the popup width differs, which is
   *  exactly why the classes come from the host rather than one stylesheet. */
  classes: Readonly<Record<string, string>>;
  /** The page tour's handle for the popup — `data-tour` on it, so a step can
   *  scope its targets to this dialog's subtree rather than the page's. */
  tour?: string;
  /** Renders the hosted form, handed the Cancel button to place in the
   *  footer's secondary slot — the same slot the drawers fill with Delete. */
  children: (cancel: ReactNode) => ReactNode;
}

/**
 * The modal chrome both LogisticsPage form dialogs share (delivery order and
 * stock entry): Base UI dialog scaffold + title + a Cancel button the hosted
 * form places in its own footer. Chrome only — each form keeps its fields,
 * validation and save.
 */
export function FormDialogShell({
  open = true,
  onClose,
  title,
  cancelLabel,
  classes,
  tour,
  children,
}: IFormDialogShellProps) {
  return (
    <Dialog.Root open={open} onOpenChange={(next) => !next && onClose()}>
      <Dialog.Portal>
        <Dialog.Backdrop className={classes.backdrop} />
        <Dialog.Popup className={classes.popup} data-tour={tour}>
          <Dialog.Title className={classes.title}>{title}</Dialog.Title>
          {/* The scrolling part — the popup is capped to the viewport and the
              title above stays in view (dialogChrome `.body`/`.bodyScroll`). */}
          <ScrollArea className={classes.body} viewportClassName={classes.bodyScroll}>
            {children(
              <button type="button" className={classes.cancelBtn} onClick={onClose}>
                {cancelLabel}
              </button>,
            )}
          </ScrollArea>
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
